// Find ways of reducing this path since we cannot index exports as they would
// be undefined at runtime.
import { IFetchAudioClipResponse } from '../../../app/api/audio/get-audio-clip';
import { TFinalSpeechToTextResponse } from '../../../app/api/google/speech-to-text';

/**
 * This takes in a string of text and submits it to the Google API that
 * converts it to an audio blob.
 *
 * TODO: Extract this out to a server side API to avoid exposing the token
 * - See `pages/api/google/speech-to-text.ts` for inspiration
 * - NOTE: This was still a work in progress at the time of writing this comment.
 *
 * @param text - The text to be converted to a blob file and played as audio.
 */
export async function convertTextToSpeechWithGoogle(text: string) {
  const url = 'https://texttospeech.googleapis.com/v1/text:synthesize';
  const headers = {
    'Content-Type': 'application/json',
    // Authorization: `Bearer ${GOOGLE_PROJECT_TOKEN}`,
    // 'X-Goog-User-Project': GOOGLE_PROJECT_ID,
  };
  const body = {
    input: { text },
    voice: {
      languageCode: 'en-US',
      ssmlGender: 'NEUTRAL',
    },
    audioConfig: {
      audioEncoding: 'OGG_OPUS', // Use uncompressed linear16 format for high quality audio
      speakingRate: 1.0, // Default speaking rate
      pitch: 0, // Default pitch
      volumeGainDb: 0, // No additional volume gain
      // Common sample rate for clear audio. Consider 44100 for even higher quality if necessary.
      sampleRateHertz: 48000,
      effectsProfileId: ['handset-class-device'], // Optimize for playback on handset-class devices
    },
  };

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (data.audioContent) {
    return `data:audio/mp3;base64,${data.audioContent}`;
  }
  throw new Error('No audio content returned from text to speech conversion.');
}

interface IFetchLongRunningSpeechRecognitionAPIOptions {
  /**
   * The base64 encoded audio string to be processed.
   */
  base64Audio: string;
  /**
   * The ID of the evaluation to be processed.
   */
  evaluationId: string;
  /**
   * The baseURL of the endpoint that will be used to validate the request session
   * since this is dynamic depending on the tenant config env.
   */
  validationBaseURL: string;
}

/**
 * Makes an API call to the Google Speech-to-Text API to start a long running
 * speech recognition job.
 */
export async function fetchLongRunningSpeechRecognitionAPI({
  base64Audio,
  evaluationId,
  validationBaseURL,
}: IFetchLongRunningSpeechRecognitionAPIOptions) {
  /**
   * The endpoint to send the audio to Google Cloud Speech-to-Text API
   */
  const SPEECH_TO_TAKE_API_URL = '/api/google/speech-to-text';

  const audioPlayer = new Audio(`data:audio/mpeg;base64,${base64Audio}`);
  audioPlayer.play();

  const response = await fetch(SPEECH_TO_TAKE_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      base64Audio,
      evaluationId,
      validationBaseURL,
    }),
  });

  const responseData = (await response.json()) as TFinalSpeechToTextResponse;

  if (!responseData.success) {
    throw responseData;
  }

  return responseData.recognizedWords;
}

interface IFetchAudioClipFromS3BucketAPIOptions {
  /**
   * The ID of the evaluation to be processed.
   */
  evaluationId: string;
  /**
   * The baseURL of the endpoint that will be used to validate the request session
   * since this is dynamic depending on the tenant config env.
   */
  validationBaseURL: string;
}

/**
 * This will call an API that randomly retrieves and an audio clip config and uses
 * it to get the matching words and an audio clip file from S3 bucket that will
 * later be played back in the browser.
 */
export async function fetchAudioClipAPI({
  evaluationId,
  validationBaseURL,
}: IFetchAudioClipFromS3BucketAPIOptions) {
  /**
   * The endpoint to fetch the audio clip from the s3 bucket
   */
  const AUDIO_CLIP_URL = '/api/audio/get-audio-clip';

  const response = await fetch(AUDIO_CLIP_URL, {
    method: 'POST',
    body: JSON.stringify({
      evaluationId,
      validationBaseURL,
    }),
  });

  const responseData = (await response.json()) as IFetchAudioClipResponse;

  if (!responseData.success) {
    throw responseData;
  }

  return responseData;
}
